import { useState, useCallback, useMemo, useEffect } from 'react'

import type { MentionData } from '../context'

const CONTEXT_MENTIONS_INCREMENT = 5

type UseContextMentionsReturn = {
  // State
  showContext: boolean
  visibleCount: number

  // Computed
  hasMore: boolean
  visibleContextMentions: MentionData[]
  showContextMentions: boolean

  // Actions
  handleToggleContext: () => void
  handleCollapseContext: () => void
  handleShowMoreContext: () => void
}

/**
 * Owns the local visibility + pagination state for a mention's ancestor
 * context. The ancestors themselves arrive preloaded from the card's single
 * on-expand conversation fetch (see `MentionCard`), so expanding the context
 * is now instant — this hook only toggles visibility and the visible slice.
 */
export const useContextMentions = (
  mentionId: string,
  contextMentions: MentionData[],
): UseContextMentionsReturn => {
  const [showContext, setShowContext] = useState(false)
  const [visibleCount, setVisibleCount] = useState(CONTEXT_MENTIONS_INCREMENT)

  const hasMore = contextMentions.length > visibleCount
  const visibleContextMentions = useMemo(
    () => contextMentions.slice(-visibleCount),
    [contextMentions, visibleCount],
  )
  const showContextMentions = showContext && contextMentions.length > 0

  // Reset when mention changes
  useEffect(() => {
    setShowContext(false)
    setVisibleCount(CONTEXT_MENTIONS_INCREMENT)
  }, [mentionId])

  const handleToggleContext = useCallback((): void => {
    setShowContext((prev) => !prev)
  }, [])

  const handleCollapseContext = useCallback((): void => {
    setShowContext(false)
    setVisibleCount(CONTEXT_MENTIONS_INCREMENT)
  }, [])

  const handleShowMoreContext = useCallback((): void => {
    setVisibleCount((prev) => prev + CONTEXT_MENTIONS_INCREMENT)
  }, [])

  return {
    showContext,
    visibleCount,
    hasMore,
    visibleContextMentions,
    showContextMentions,
    handleToggleContext,
    handleCollapseContext,
    handleShowMoreContext,
  }
}
